'use client';

import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import StatusBar from 'src/components/primitives/StatusBar/StatusBar';
import { useRef, useState, useEffect } from 'react';
import useAccountExists from 'src/hooks/useAccountExists';
import useVelocityClient from 'src/hooks/useVelocityClient';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';

const ReferralBar = () => {
	const referrerParam = useVelocityStore((s) => s.referrerParam);
	const referrerParamFailed = useVelocityStore((s) => s.referrerParamFailed);

	const velocityClient = useVelocityClient();
	const alreadyDoneNotificationLoop = useRef(false);
	const accountExists = useAccountExists();
	const isSubscribed = useVelocityClientIsReady();

	const [noticeText, setNoticeText] = useState('');

	const showSuccess = () => {
		const stateAccount = velocityClient?.getStateAccount();
		const feeTier = stateAccount?.perpFeeStructure?.feeTiers?.[0];
		const refereeDiscount =
			((feeTier?.refereeFeeNumerator ?? 0) /
				(feeTier?.refereeFeeDenominator ?? 0)) *
			100;

		setNoticeText(
			`⚡️ Referral code: ${referrerParam} ⚡️ Connect your wallet and deposit to save ${refereeDiscount}% off trading fees.`
		);
	};

	const showFailure = (userAccountExists: boolean) => {
		if (userAccountExists) {
			setNoticeText(
				`Referral failed; you can't apply a referral code to a pre-existing Velocity account.`
			);
		} else {
			setNoticeText(
				`Referral failed; the referral code requested was not found.`
			);
		}
	};

	// When referral state is reset after successful account creation, clear the banner.
	useEffect(() => {
		if (!referrerParam && !referrerParamFailed) {
			setNoticeText('');
			alreadyDoneNotificationLoop.current = false;
		}
	}, [referrerParam, referrerParamFailed]);

	/**
	 * There are three cases to handle:
	 * => Completely new, never connected wallet : want to show "success"
	 * => Connected wallet before but not deposited : want to show "success" (the account only gets created upon deposit)
	 * => Connected wallet and deposited before : want to show "warning"
	 *
	 * Also only want to run this logic once, hence the alreadyDoneNoticicationLoop flag.
	 */
	useEffect(() => {
		if (
			alreadyDoneNotificationLoop.current ||
			!velocityClient ||
			!velocityClient.isSubscribed
		) {
			return;
		}

		// if referrer param
		// and velocityClient is subscribed
		if ((referrerParam || referrerParamFailed) && velocityClient.isSubscribed) {
			alreadyDoneNotificationLoop.current = true;

			// referrerParamFailed is set by handleReferralPageLoad when the code is
			// invalid or the account already existed at check time.
			if (referrerParamFailed) {
				showFailure(accountExists);
				return;
			}

			// else => referral code successfully applied
			showSuccess();
		}
	}, [
		velocityClient,
		isSubscribed,
		referrerParam,
		accountExists,
		referrerParamFailed,
	]);

	if (!noticeText) {
		return;
	}

	return (
		<StatusBar
			variant="custom"
			barClassName="bg-positive-green-secondary-bg text-text-default"
			closeButtonClassName="text-text-default"
		>
			{noticeText}
		</StatusBar>
	);
};

export default ReferralBar;
